import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Droplets, Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Cache of in-flight promises so React.StrictMode double-mount only fires ONE request.
const verifyPromises = new Map();

function callVerify(token) {
  if (!verifyPromises.has(token)) {
    verifyPromises.set(token, api.post("/auth/verify-email", { token }));
  }
  return verifyPromises.get(token);
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(token ? "loading" : "missing"); // loading | success | error | missing
  const [message, setMessage] = useState("");
  // Keep latest refreshUser in a ref so the effect doesn't re-run when AuthContext re-renders
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;

  useEffect(() => {
    if (!token) return;
    let canceled = false;
    callVerify(token)
      .then(({ data }) => {
        if (canceled) return;
        setStatus("success");
        setMessage(data.message || "Email verified.");
        refreshUserRef.current?.();
      })
      .catch((err) => {
        if (canceled) return;
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Verification failed.");
      });
    return () => { canceled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#F3F4F1]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#064E3B] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-semibold">IV Therapy Finder</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center shadow-sm" data-testid={`verify-${status}`}>
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#064E3B] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-semibold text-stone-900">Verifying your email…</h1>
              <p className="text-sm text-stone-600 mt-2">One moment, please.</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/15 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-[#10B981]" />
              </div>
              <h1 className="font-display text-2xl font-semibold text-stone-900">You're all set!</h1>
              <p className="text-sm text-stone-600 mt-2">{message}</p>
              <Link to="/" className="inline-block mt-6 text-sm font-semibold text-[#064E3B] hover:underline" data-testid="verify-home-link">
                Continue to IV Therapy Finder →
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                <XCircle className="w-9 h-9 text-rose-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold text-stone-900">Verification failed</h1>
              <p className="text-sm text-stone-600 mt-2">{message}</p>
              <Link to="/dashboard/user" className="inline-block mt-6 text-sm font-semibold text-[#064E3B] hover:underline">
                Go to your dashboard to request a new link →
              </Link>
            </>
          )}
          {status === "missing" && (
            <>
              <h1 className="font-display text-2xl font-semibold text-stone-900">No token provided</h1>
              <p className="text-sm text-stone-600 mt-2">This page expects a verification token in the URL. Please use the link in your verification email.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
